import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, MapPin, Tag, Globe, Languages, Clock, ExternalLink, Handshake, Building2 } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { PARTNER_TYPES } from "@/i18n/translations";
import { useSeo } from "@/lib/seo";
import VerifiedBadge from "@/components/VerifiedBadge";

export default function PartnerDetail() {
  const { slug } = useParams();
  const { lang, L, t } = useLang();
  const navigate = useNavigate();
  const [o, setO] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => { api.get(`/partners/${slug}`).then(({ data }) => setO(data)).catch(() => setError(true)); }, [slug]);
  useSeo({ title: o ? o.organization : "", description: o ? L(o, "summary") : "", lang, image: o?.logo || o?.cover_image, type: "profile" });

  if (error) return <div className="mx-auto max-w-3xl px-6 py-24 text-center text-anthracite/60">404</div>;
  if (!o) return <div className="mx-auto max-w-3xl px-6 py-24 text-center text-anthracite/40">…</div>;
  const v = o.verification || {};
  const Row = ({ icon: Icon, label, value }) => value ? (
    <div className="flex items-start gap-2 text-sm"><Icon className="mt-0.5 h-4 w-4 shrink-0 text-champagne" /><span className="text-anthracite/50">{label} :</span><span className="text-anthracite/80">{value}</span></div>
  ) : null;
  const S = ({ title, body }) => body ? (<div className="mb-8"><h3 className="mb-2 font-serif text-xl text-anthracite">{title}</h3><p className="whitespace-pre-line text-base leading-relaxed text-anthracite/80">{body}</p></div>) : null;

  return (
    <article className="mx-auto max-w-4xl px-6 py-12 lg:px-10">
      <button onClick={() => navigate("/partenaires")} className="mb-8 inline-flex items-center gap-2 text-sm text-anthracite/60 hover:text-champagne"><ArrowLeft className="h-4 w-4" /> {lang === "fr" ? "Retour aux partenaires" : "Back to partners"}</button>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <span className="text-xs uppercase tracking-[0.2em] text-champagne">{PARTNER_TYPES.find((x) => x.value === o.partner_type)?.[lang]}</span>
        <VerifiedBadge verified={o.rst_verified} size="lg" />
      </div>
      <div className="mb-6 flex items-center gap-4">
        {o.logo ? <img src={o.logo} alt="" className="h-16 w-16 shrink-0 rounded border border-tertiary object-contain" /> : <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded border border-tertiary bg-sand"><Building2 className="h-6 w-6 text-anthracite/30" /></div>}
        <h1 className="font-serif text-4xl leading-tight tracking-tight text-anthracite sm:text-5xl">{o.organization}</h1>
      </div>
      {L(o, "summary") && <p className="mb-8 text-lg leading-relaxed text-anthracite/70">{L(o, "summary")}</p>}

      <div className="mb-8 grid gap-3 border-y border-tertiary py-5 sm:grid-cols-2">
        <Row icon={Handshake} label={lang === "fr" ? "Type" : "Type"} value={PARTNER_TYPES.find((x) => x.value === o.partner_type)?.[lang]} />
        <Row icon={Tag} label={t("sector")} value={o.sector} />
        <Row icon={MapPin} label={lang === "fr" ? "Pays / marché" : "Country / market"} value={[o.region, o.country].filter(Boolean).join(", ")} />
        <Row icon={Globe} label={lang === "fr" ? "Zones" : "Zones"} value={(o.zones || []).join(", ")} />
        <Row icon={Languages} label={lang === "fr" ? "Langues" : "Languages"} value={(o.languages || []).join(", ")} />
      </div>

      <S title={lang === "fr" ? "Présentation" : "Overview"} body={L(o, "description")} />
      <S title={lang === "fr" ? "Produits / services" : "Products / services"} body={L(o, "products_services")} />
      <S title={lang === "fr" ? "Capacités" : "Capacities"} body={L(o, "capacities")} />
      <S title={lang === "fr" ? "Marchés" : "Markets"} body={L(o, "markets")} />
      <S title={lang === "fr" ? "Besoins" : "Needs"} body={L(o, "needs")} />
      <S title={lang === "fr" ? "Offres" : "Offers"} body={L(o, "offers")} />
      <S title={lang === "fr" ? "Collaboration recherchée" : "Collaboration sought"} body={L(o, "collaboration")} />
      <S title={lang === "fr" ? "Références" : "References"} body={L(o, "references")} />

      <div className="mb-6" data-testid="verification-block" />

      <p className="mb-10 text-xs text-anthracite/50">{lang === "fr" ? "La vérification RSTI réduit l'incertitude sans constituer une garantie d'absence de risque. Les coordonnées et documents confidentiels ne sont pas publics : RSTI assure la mise en relation." : "RSTI verification reduces uncertainty without guaranteeing the absence of risk. Confidential contact details and documents are not public: RSTI handles the introduction."}</p>

      <div className="flex flex-col gap-3 border-t border-tertiary pt-8 sm:flex-row">
        <Link to={`/connect?category=partners&ref=${encodeURIComponent(o.organization)}`} data-testid="cta-contact-rst" className="flex-1 bg-anthracite px-6 py-4 text-center text-sm font-medium text-ivory transition-colors hover:bg-black">{lang === "fr" ? "Contacter RSTI pour une mise en relation" : "Contact RSTI for an introduction"}</Link>
      </div>
    </article>
  );
}
